import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const adminToken = localStorage.getItem("kcis_admin_token");
  const clientToken = localStorage.getItem("kcis_client_token");
  const intakeToken = sessionStorage.getItem("kcis_assessment_token");
  // Admin and client sessions are mutually exclusive in this app.
  // Prefer admin token if present; fall back to client token, then the
  // single-submission intake token (public intake → own report only).
  if (adminToken) {
    config.headers.Authorization = `Bearer ${adminToken}`;
  } else if (clientToken) {
    config.headers.Authorization = `Bearer ${clientToken}`;
  } else if (intakeToken) {
    config.headers.Authorization = `Bearer ${intakeToken}`;
  }
  return config;
});

// Query-string auth for direct links (PDF) that cannot send headers.
export const authQuery = () => {
  const t = localStorage.getItem("kcis_admin_token") || localStorage.getItem("kcis_client_token") || sessionStorage.getItem("kcis_assessment_token");
  return t ? `token=${encodeURIComponent(t)}` : "";
};

// ─── Phase 3 — subscription_required hard-gate interceptor ───────────────
// Backend middleware returns 403 with detail.code === "subscription_required"
// for any blocked practitioner request. We catch this globally and route
// the user to /subscribe so they never hit a dead end.
const _subscriptionGateInterceptor = (error) => {
  try {
    const detail = error?.response?.data?.detail;
    if (
      error?.response?.status === 403 &&
      detail &&
      typeof detail === "object" &&
      detail.code === "subscription_required"
    ) {
      // Don't redirect when already on /subscribe (prevents loops)
      const path = typeof window !== "undefined" ? window.location.pathname : "";
      if (path !== "/subscribe") {
        const redirect = detail.redirect || "/subscribe";
        window.location.replace(redirect);
      }
    }
  } catch (_) { /* swallow — never break the original error chain */ }
  return Promise.reject(error);
};
axios.interceptors.response.use((r) => r, _subscriptionGateInterceptor);
api.interceptors.response.use((r) => r, _subscriptionGateInterceptor);

// ─── Phase 7C — global 401 handler for admin surfaces ────────────────────
// On any 401 from an admin-scoped API call (i.e. the request that just fired
// carried an admin token), clear the stale token and bounce the browser to
// /admin/login with a return-to marker. This turns "infinite Loading…" and
// generic error toasts on admin pages into an actionable re-login prompt.
const _adminExpiredInterceptor = (error) => {
  try {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const hadAdminToken = (error?.config?.headers?.Authorization || "").includes("Bearer ");
    // Never redirect for public/student endpoints — only for admin+kcis routes
    const isAdminScoped = /\/(kcis\/admin|admin|auth\/admin)/.test(url) || url.startsWith("/kcis/admin");
    if (status === 401 && hadAdminToken && isAdminScoped) {
      const path = typeof window !== "undefined" ? window.location.pathname + window.location.search : "";
      // Do not redirect if we are already on the sign-in surface
      if (!/^\/(admin\/login|admin\/forgot-password|admin\/accept-invite)/.test(path)) {
        try { adminAuth.clear(); } catch (_e) { /* noop */ }
        const next = encodeURIComponent(path || "/kcis/admin");
        window.location.replace(`/admin/login?expired=1&next=${next}`);
      }
    }
  } catch (_e) { /* never break the original error chain */ }
  return Promise.reject(error);
};
api.interceptors.response.use((r) => r, _adminExpiredInterceptor);

export const adminAuth = {
  setSession: ({ token, email, role, practitioner_id, name, subscription_tier }) => {
    localStorage.setItem("kcis_admin_token", token);
    localStorage.setItem("kcis_admin_email", email || "");
    localStorage.setItem("kcis_admin_role", role || "super_admin");
    localStorage.setItem("kcis_admin_practitioner_id", practitioner_id || "");
    localStorage.setItem("kcis_admin_name", name || "");
    // Phase 6A — store the practitioner's subscription edition (display-only).
    // Empty string = no edition line should be shown (super_admin / unknown).
    localStorage.setItem("kcis_admin_edition", subscription_tier || "");
  },
  setToken: (t, email) => {
    // backwards-compatible setter
    localStorage.setItem("kcis_admin_token", t);
    localStorage.setItem("kcis_admin_email", email);
  },
  getToken: () => localStorage.getItem("kcis_admin_token"),
  getRole: () => localStorage.getItem("kcis_admin_role") || "super_admin",
  getPractitionerId: () => localStorage.getItem("kcis_admin_practitioner_id") || "",
  getEmail: () => localStorage.getItem("kcis_admin_email") || "",
  getName: () => localStorage.getItem("kcis_admin_name") || "",
  getEdition: () => localStorage.getItem("kcis_admin_edition") || "",
  isSuperAdmin: () => (localStorage.getItem("kcis_admin_role") || "super_admin") === "super_admin",
  isPro: () => {
    const r = localStorage.getItem("kcis_admin_role") || "super_admin";
    return r === "super_admin" || r === "practitioner_pro";
  },
  clear: () => {
    ["kcis_admin_token","kcis_admin_email","kcis_admin_role","kcis_admin_practitioner_id","kcis_admin_name","kcis_admin_edition"]
      .forEach(k => localStorage.removeItem(k));
  },
};

export const clientAuth = {
  setSession: (t, clientId, name, email) => {
    localStorage.setItem("kcis_client_token", t);
    localStorage.setItem("kcis_client_id", clientId);
    localStorage.setItem("kcis_client_name", name);
    localStorage.setItem("kcis_client_email", email);
  },
  getId: () => localStorage.getItem("kcis_client_id"),
  getName: () => localStorage.getItem("kcis_client_name"),
  getEmail: () => localStorage.getItem("kcis_client_email"),
  clear: () => {
    localStorage.removeItem("kcis_client_token");
    localStorage.removeItem("kcis_client_id");
    localStorage.removeItem("kcis_client_name");
    localStorage.removeItem("kcis_client_email");
  },
};
